import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Share2, Link2, Palette, ArrowRight, Zap, Layers, Globe, Sparkles } from 'lucide-react';
import { applySeo, resolveMarketingPreviewSeo } from '../../utils/seo';
import { ogBrandImageUrl } from '../../constants/ogBrand';
import { useSiteBranding } from '../../context/SiteBrandingContext';
import { normalizeUiLang } from '../../utils/localePreference';
import { digitalCardStyles } from './digitalCardStyles';
import DigitalCardNav from './DigitalCardNav';
import DigitalCardFooter from './DigitalCardFooter';
import PreviewPhoneFrame from './PreviewPhoneFrame';
import TcardsShowcaseSection from './TcardsShowcaseSection';
import DigitalCardPricingSection, { type DigitalCardPricingPlan } from './DigitalCardPricingSection';
import { SHOWCASE_BUSINESS_PREVIEW } from './templatePreviewAssets';
import { PRICING_SECTION_ID, scrollToDigitalCardSection, TEMPLATES_CHOOSE_URL } from './digitalCardRoutes';

function normalizePricingPlans(raw: unknown): DigitalCardPricingPlan[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((plan) => plan && typeof plan === 'object')
    .map((plan: any) => ({
      name: String(plan.name || '').trim(),
      price: String(plan.price || '').trim(),
      description: String(plan.description || '').trim(),
      features: Array.isArray(plan.features) ? plan.features.map((f: unknown) => String(f || '').trim()).filter(Boolean) : [],
      buttonText: String(plan.buttonText || 'Get Started').trim(),
      buttonColor: String(plan.buttonColor || '').trim(),
      isPopular: !!plan.isPopular,
    }))
    .filter((plan) => plan.name);
}

const DigitalCardLanding: React.FC = () => {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { homepageSettings, siteSeo } = useSiteBranding();
  const resolvedLang = normalizeUiLang(i18n.resolvedLanguage || i18n.language);
  const [plans, setPlans] = useState<DigitalCardPricingPlan[]>([]);
  const [pricingTitle, setPricingTitle] = useState('');
  const [pricingSubtitle, setPricingSubtitle] = useState('');

  useEffect(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://www.tcards.org';
    const defaults = {
      title: t('dcl_seo_title', { defaultValue: 'TCards Digital Business Card | Your Professional Digital Identity' }),
      description: t('dcl_seo_desc', {
        defaultValue:
          'Create a professional TCards Digital Business Card with your bio, contact information, social profiles, business links and more. Choose a template, personalize your TCard and share it anywhere.',
      }),
      keywords: ['Digital Business Card', 'Digital Card', 'Online Business Card', 'Professional Digital Card', 'Virtual Business Card', 'Digital Profile'],
    };
    const seo = resolveMarketingPreviewSeo(
      homepageSettings.previews as Record<string, unknown> | undefined,
      'digitalCard',
      origin,
      defaults,
      siteSeo,
    );
    const canonical = `${origin}/digital-card`;
    const dispose = applySeo({
      title: seo.title,
      description: seo.description,
      keywords: seo.keywords.length ? seo.keywords : defaults.keywords,
      image: seo.image || ogBrandImageUrl(origin, 'site'),
      url: canonical,
      canonicalUrl: canonical,
      siteName: 'TCards Digital Business Card',
      twitterHandle: seo.twitterHandle,
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: seo.title,
        url: canonical,
        description: seo.description,
      },
    });
    return dispose;
  }, [t, resolvedLang, homepageSettings.previews, siteSeo]);

  useEffect(() => {
    (async () => {
      try {
        const [plansRes, settingsRes] = await Promise.all([
          fetch('/api/public/subscription-plans'),
          fetch('/api/site-settings'),
        ]);
        if (plansRes.ok) {
          const data = await plansRes.json();
          setPlans(normalizePricingPlans(data));
        }
        if (settingsRes.ok) {
          const data = await settingsRes.json();
          const pricing = data?.homepageSettings?.pricing || {};
          setPricingTitle(String(pricing.title || '').trim());
          setPricingSubtitle(String(pricing.subtitle || '').trim());
        }
      } catch {}
    })();
  }, []);

  const scrollToPricing = useCallback(() => {
    scrollToDigitalCardSection(PRICING_SECTION_ID);
  }, []);

  useEffect(() => {
    if (location.hash !== `#${PRICING_SECTION_ID}`) {
      if (!location.hash) window.scrollTo(0, 0);
      return;
    }

    const tryScroll = () => {
      const el = document.getElementById(PRICING_SECTION_ID);
      if (!el) return false;
      scrollToPricing();
      return true;
    };

    if (tryScroll()) return;

    const retry = window.setInterval(() => {
      if (tryScroll()) window.clearInterval(retry);
    }, 100);
    const stop = window.setTimeout(() => window.clearInterval(retry), 3000);
    return () => {
      window.clearInterval(retry);
      window.clearTimeout(stop);
    };
  }, [location.pathname, location.hash, plans.length, scrollToPricing]);

  const pillars = [
    {
      icon: Link2,
      bar: 'blue',
      iconTone: 'blue',
      titleKey: 'dcl_pillar_connect_title',
      descKey: 'dcl_pillar_connect_desc',
      titleDefault: 'Connect everything you share',
      descDefault: 'Profile, bio, contacts, social profiles and links — all in one polished digital card.',
    },
    {
      icon: Share2,
      bar: 'violet',
      iconTone: 'violet',
      titleKey: 'dcl_pillar_share_title',
      descKey: 'dcl_pillar_share_desc',
      titleDefault: 'Share anywhere instantly',
      descDefault: 'Send your TCard via WhatsApp, email, LinkedIn, QR code or any app you already use.',
    },
    {
      icon: Palette,
      bar: 'emerald',
      iconTone: 'emerald',
      titleKey: 'dcl_pillar_design_title',
      descKey: 'dcl_pillar_design_desc',
      titleDefault: 'Designs that feel like you',
      descDefault: 'Pick a Business or Artist template, personalize it in minutes, and make a memorable first impression.',
    },
  ];

  const steps = [
    { n: 1, titleKey: 'dcl_step1_title', descKey: 'dcl_step1_desc' },
    { n: 2, titleKey: 'dcl_step2_title', descKey: 'dcl_step2_desc' },
    { n: 3, titleKey: 'dcl_step3_title', descKey: 'dcl_step3_desc' },
    { n: 4, titleKey: 'dcl_step4_title', descKey: 'dcl_step4_desc' },
  ];

  const platforms = ['LinkedIn', 'Instagram', 'WhatsApp', 'Website', 'Facebook', 'TikTok', 'YouTube', 'X'];

  return (
    <div className="dcl-root dcl-root--linktree">
      <style>{digitalCardStyles}</style>
      <DigitalCardNav activePath="/digital-card" />

      {/* Hero */}
      <section className="dcl-hero-lt">
        <div className="dcl-container dcl-hero-lt-inner">
          <div className="dcl-hero-lt-copy">
            <div className="dcl-section-badge">
              <Zap size={14} />
              {t('dcl_nav_tagline', { defaultValue: 'Digital Business Card' })}
            </div>
            <h1 className="dcl-hero-lt-title">
              {t('dcl_hero_title', { defaultValue: 'Your Digital Identity, Built Around You.' })}
            </h1>
            <p className="dcl-hero-lt-sub">
              {t('dcl_hero_sub', {
                defaultValue:
                  'Create one professional TCard to introduce yourself, share your contacts, social profiles and business links — all from one beautifully designed digital card.',
              })}
            </p>
            <div className="dcl-hero-lt-actions">
              <Link to={TEMPLATES_CHOOSE_URL} className="dcl-btn dcl-btn--primary dcl-btn--pill">
                {t('dcl_nav_get_started', { defaultValue: 'Get Started' })}
                <ArrowRight size={18} />
              </Link>
            </div>
            <p className="dcl-signin-link dcl-signin-link--hero">
              {t('dcl_already_account', { defaultValue: 'Already have an account?' })}{' '}
              <Link to="/auth?login=1">{t('dcl_nav_sign_in', { defaultValue: 'Sign In' })}</Link>
            </p>
          </div>
          <div className="dcl-hero-lt-visual">
            <PreviewPhoneFrame
              src={SHOWCASE_BUSINESS_PREVIEW}
              alt={t('dcl_showcase_business_alt', { defaultValue: 'TCards Business digital card example' })}
              size="lg"
              className="dcl-hero-lt-phone"
            />
          </div>
        </div>
      </section>

      {/* Dual-phone showcase */}
      <TcardsShowcaseSection />

      {/* Three pillars */}
      <section className="dcl-section dcl-section--white dcl-section--mesh">
        <div className="dcl-container">
          <div className="dcl-section-header">
            <div className="dcl-section-badge">
              <Layers size={14} />
              {t('dcl_pillars_badge', { defaultValue: 'Why TCards' })}
            </div>
            <h2 className="dcl-section-title dcl-section-title--center">
              {t('dcl_pillars_heading', { defaultValue: 'The digital card built for how you connect.' })}
            </h2>
          </div>
          <div className="dcl-pillar-grid">
            {pillars.map(({ icon: Icon, bar, iconTone, titleKey, descKey, titleDefault, descDefault }) => (
              <article key={titleKey} className="dcl-pillar-card">
                <div className={`dcl-pillar-card-bar dcl-pillar-card-bar--${bar}`} aria-hidden="true" />
                <div className="dcl-pillar-card-body">
                  <div className={`dcl-pillar-icon dcl-pillar-icon--${iconTone}`}><Icon size={22} strokeWidth={2.2} /></div>
                  <h3 className="dcl-pillar-title">{t(titleKey, { defaultValue: titleDefault })}</h3>
                  <p className="dcl-pillar-desc">{t(descKey, { defaultValue: descDefault })}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Platforms */}
      <section className="dcl-section dcl-section--lime dcl-section--mesh">
        <div className="dcl-container dcl-container--narrow" style={{ textAlign: 'center' }}>
          <div className="dcl-section-badge">
            <Globe size={14} />
            {t('dcl_presence_badge', { defaultValue: 'Your Presence' })}
          </div>
          <h2 className="dcl-section-title dcl-section-title--center dcl-section-title--forest">
            {t('dcl_presence_title', { defaultValue: 'Bring your digital presence together' })}
          </h2>
          <p className="dcl-section-sub dcl-section-sub--center dcl-section-sub--forest" style={{ marginBottom: 32 }}>
            {t('dcl_presence_sub', {
              defaultValue:
                'Your professional information is often spread across several platforms. TCards brings the important parts together into one identity.',
            })}
          </p>
          <div className="dcl-chip-row">
            {platforms.map((p) => (
              <span key={p} className="dcl-chip dcl-chip--forest">{p}</span>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="dcl-section dcl-section--white dcl-section--mesh">
        <div className="dcl-container">
          <div className="dcl-section-header">
            <div className="dcl-section-badge">
              <Sparkles size={14} />
              {t('dcl_how_badge', { defaultValue: 'How It Works' })}
            </div>
            <h2 className="dcl-section-title dcl-section-title--center">
              {t('dcl_how_title', { defaultValue: 'Create yours in a few simple steps.' })}
            </h2>
          </div>
          <div className="dcl-steps-lt">
            {steps.map(({ n, titleKey, descKey }) => (
              <div key={n} className="dcl-step-lt">
                <span className="dcl-step-lt-num">{String(n).padStart(2, '0')}</span>
                <h3 className="dcl-step-lt-title">{t(titleKey)}</h3>
                <p className="dcl-step-lt-desc">{t(descKey)}</p>
              </div>
            ))}
          </div>
          <div className="dcl-section-cta-row">
            <Link to={TEMPLATES_CHOOSE_URL} className="dcl-btn dcl-btn--primary dcl-btn--pill">
              {t('dcl_cta_explore_templates', { defaultValue: 'Explore Templates' })}
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Pricing — section anchor always present so nav hash links can scroll */}
      <section
        className="dcl-section dcl-section--soft dcl-section--mesh dcl-scroll-target"
        id={PRICING_SECTION_ID}
      >
        <div className="dcl-container">
          <DigitalCardPricingSection plans={plans} title={pricingTitle} subtitle={pricingSubtitle} />
        </div>
      </section>

      {/* Final CTA */}
      <section className="dcl-section dcl-final-lt">
        <div className="dcl-container dcl-container--narrow">
          <h2 className="dcl-final-lt-title">
            {t('dcl_final_title', { defaultValue: 'Make your digital introduction count.' })}
          </h2>
          <p className="dcl-final-lt-sub">
            {t('dcl_final_sub', {
              defaultValue:
                'Choose a template, personalize your TCard, and share one link that tells people who you are.',
            })}
          </p>
          <Link to={TEMPLATES_CHOOSE_URL} className="dcl-btn dcl-btn--primary dcl-btn--pill dcl-btn--lg">
            {t('dcl_nav_get_started', { defaultValue: 'Get Started' })}
            <ArrowRight size={18} />
          </Link>
          <p className="dcl-signin-link dcl-signin-link--final">
            {t('dcl_already_account', { defaultValue: 'Already have an account?' })}{' '}
            <Link to="/auth?login=1">{t('dcl_nav_sign_in', { defaultValue: 'Sign In' })}</Link>
          </p>
        </div>
      </section>

      <DigitalCardFooter />
    </div>
  );
};

export default DigitalCardLanding;
